/**
 * FileViewerModal Component
 * 
 * Modal to view file content with syntax highlighting using Monaco Editor.
 * Shows:
 * - File name and path
 * - Change type badge (Added/Modified/Deleted)
 * - File content with syntax highlighting
 * - Copy and close buttons
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import Editor from '@monaco-editor/react';
import { FileChangeNode, FileChangeType } from '../../../../types/AppPanel';

interface FileViewerModalProps {
  /** The file node being viewed */
  node: FileChangeNode;
  /** File content (decoded) */
  content: string | null;
  /** Whether content is loading */
  isLoading: boolean;
  /** Close modal callback */
  onClose: () => void;
}

/**
 * Get language from filename extension
 */
function getLanguage(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'js': case 'mjs': return 'javascript';
    case 'ts': case 'tsx': return 'typescript';
    case 'jsx': return 'javascript';
    case 'json': return 'json';
    case 'html': case 'htm': return 'html';
    case 'css': return 'css';
    case 'scss': case 'sass': return 'scss';
    case 'md': case 'markdown': return 'markdown';
    case 'xml': return 'xml';
    case 'yaml': case 'yml': return 'yaml';
    case 'sh': case 'bash': return 'shell';
    case 'sql': return 'sql';
    default: return 'plaintext';
  }
}

/**
 * Get change type from node type
 */
function getChangeType(nodeType: string): FileChangeType {
  switch (nodeType) {
    case 'green-file': return 'added';
    case 'yellow-file': return 'modified';
    case 'red-file': return 'deleted';
    default: return 'unchanged';
  }
}

/**
 * Get badge styles for change type
 */
function getChangeBadge(changeType: FileChangeType): { bg: string; text: string; label: string } {
  switch (changeType) {
    case 'added':
      return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Added' };
    case 'modified':
      return { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Modified' };
    case 'deleted':
      return { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Deleted' };
    default:
      return { bg: 'bg-white/10', text: 'text-white/60', label: 'Unchanged' };
  }
}

export default function FileViewerModal({
  node,
  content,
  isLoading,
  onClose,
}: FileViewerModalProps) {
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(true); // Default to fullscreen for better view
  
  const filename = node.data?.filename || node.text;
  const language = getLanguage(node.text);
  const changeType = getChangeType(node.type);
  const badge = getChangeBadge(changeType);

  // Dynamic editor height based on fullscreen mode
  const editorHeight = isFullscreen ? 'calc(100vh - 120px)' : '60vh';

  // Handle copy
  const handleCopy = () => {
    if (content) {
      navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Use portal to render modal outside of component hierarchy
  // This ensures it covers the entire screen, not just the parent component
  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      style={{ padding: isFullscreen ? 0 : '1rem' }}
      onClick={handleBackdropClick}
    >
      <div 
        className={`
          bg-[#1e1e1e] overflow-hidden shadow-2xl border border-white/10 flex flex-col
          ${isFullscreen 
            ? 'w-full h-full' 
            : 'w-full max-w-4xl max-h-[90vh] rounded-xl'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-[#252526] border-b border-[#3c3c3c]">
          <div className="flex items-center gap-3 min-w-0">
            {/* File Icon */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-white/50 flex-shrink-0">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="2"/>
              <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2"/>
            </svg>
            
            {/* Filename */}
            <span className="text-sm font-medium text-white truncate">{filename}</span>
            
            {/* Change Badge */}
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase flex-shrink-0 ${badge.bg} ${badge.text}`}>
              {badge.label}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Fullscreen Toggle Button */}
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors flex-shrink-0"
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? (
                // Exit fullscreen icon (minimize)
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"/>
                </svg>
              ) : (
                // Fullscreen icon (maximize)
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/>
                </svg>
              )}
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors flex-shrink-0"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center gap-3 text-white/50">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Loading file...</span>
              </div>
            </div>
          ) : content ? (
            <Editor
              height={editorHeight}
              language={language}
              value={content}
              theme="vs-dark"
              options={{
                readOnly: true,
                minimap: { enabled: isFullscreen },
                fontSize: 13,
                fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                padding: { top: 16, bottom: 16 },
                scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
                wordWrap: 'on',
                folding: true,
                renderLineHighlight: 'none',
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-64 text-white/40">
              <div className="text-center">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="mx-auto mb-2 text-white/20">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <p>Failed to load file content</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 bg-[#252526] border-t border-[#3c3c3c]">
          <button
            onClick={handleCopy}
            disabled={!content}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm
              transition-colors
              ${copied 
                ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20' 
                : 'text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10'}
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            {copied ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                </svg>
                Copy content
              </>
            )}
          </button>
          <button
            onClick={onClose}
            className="
              px-3 py-1.5 rounded-md text-sm font-medium
              text-white bg-[#208AEC] hover:bg-[#208AEC]/80
              transition-colors
            "
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

